import { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ChevronForwardOutlineIc, ChevronBackOutlineIc } from '@dsch/dd-icons';

import {
  Next,
  Previous,
} from 'components/Toolkit/Gallery/components/GalleryUiElements';
import { AdSummary } from 'types';
import { CadCard } from 'components/PageWrappers/SharedComponents/CadCard/CadCard';
import type {
  CardOrientation,
  CardVariations,
} from 'components/Toolkit/Card/Card';

import convertToFriendlyPath from 'utils/FriendlyPath';
import { useCampaignURLHandler } from 'components/PageWrappers/Hooks/Campaigns/UseCampaignURLHandler';
import { accessibleOnClick } from 'utils/Accessibility';

interface SimpleCarouselProps {
  ads: AdSummary[];
  cdnUrl: string;
  domain: string;
  variant?: CardVariations;
  orientation?: CardOrientation;
  /** The number of cards the in the carousel */
  numberOfCards?: 6 | 8 | 10;
  cardTrackingId?: string;
  useChevrons?: boolean;
  testId?: string;
}

const Wrapper = styled.div`
  position: relative;
`;

const List = styled.ul<{ variant: CardVariations; numberOfCards: number }>`
  /* Padding & Margin is to stop cropping bottom of cards */
  padding: 4px 0;
  margin: -4px 0;
  display: grid;
  grid-gap: ${({ theme }) => theme.spacing.M16};
  grid-template-rows: 1fr;
  overflow-x: auto;
  scroll-behavior: smooth;
  /*
    We set 0 and 1px so that there is invisible items in the grid and they get grid-gap spacing.
    This means the cards are not touching the side and essentially have padding left & right
   */
  grid-template-columns: 0 ${({ numberOfCards }) =>
      `repeat(${numberOfCards}, ${
        numberOfCards === 8 ? '130px' : '180px'
      })`} 1px;

  /* As mentioned above, these are the invisible grid items */
  &:before,
  &:after {
    content: '';
    @media (min-width: 640px) {
      display: none;
    }
  }

  /* 
    As we scale up to desktop we are no longer bleeding to the edge so cards
    so they would get cut off from column padding. We drop carousel in this case
  */
  @media (min-width: 640px) {
    grid-template-columns: ${({ numberOfCards }) =>
      `repeat(${numberOfCards}, 1fr)`};

    /* Turn off all cards so we can only X number at certain breakpoints */
    li {
      display: none;
    }
  }

  ${({ numberOfCards }) =>
    numberOfCards !== 10
      ? `@media (min-width: 640px) and (max-width: 841px) {
    grid-template-columns: repeat(4, 1fr);
    li:nth-child(-n + 4) {
      display: initial;
    }
  }

  @media (min-width: 841px) and (max-width: 960px) {
    grid-template-columns: repeat(5, 1fr);
    li:nth-child(-n + 5) {
      display: initial;
    }
  }

  @media (min-width: 961px) and (max-width: 1200px) {
    grid-template-columns: repeat(6, 1fr);
    li:nth-child(-n + 6) {
      display: initial;
    }
  }
  
  @media (min-width: 1201px) {
    /* On desktop we want to stop sides cropping */
    padding: 4px;
    margin: -4px;
    li {
      display: initial;
    }
  }
  `
      : `
      
      @media (min-width: 640px) {
      padding: 4px;
      margin: -4px;
      grid-template-columns: repeat(10, 180px) 1px;
      overflow-x: hidden;
      ::-webkit-scrollbar {
          display: none;
      }
      li {
        display: initial;
      }
      &:after {
        display: block;
      }
  }`}
`;

const SPrevious = styled(Previous)`
  display: none;

  @media (min-width: 640px) {
    margin-left: ${({ theme }) => `-${theme.spacing.S8}`};
    display: block;
  }
`;

const SNext = styled(Next)`
  display: none;

  @media (min-width: 640px) {
    display: block;
  }
`;

const CardListItem = styled.li`
  cursor: pointer;
`;

function SimpleCarousel({
  ads,
  cdnUrl,
  domain,
  variant = 'SIMPLE',
  orientation = 'VERTICAL',
  numberOfCards = 8,
  cardTrackingId = '',
  useChevrons = false,
  testId,
}: SimpleCarouselProps) {
  const [scrollIndex, setScrollIndex] = useState<number>(0);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [carouselCutoff, setCarouselCutoff] = useState<number>(0);
  const ref = useRef<HTMLUListElement>(null);
  const SCROLL_DISTANCE = 196;
  const TOTAL_SCROLL_DISTANCE = 784;

  useEffect(() => {
    if (window.innerWidth < 1200) {
      setCarouselCutoff(1200 - window.innerWidth);
    }
  }, []);

  const { handleCampaignAdRouterPush } = useCampaignURLHandler();

  function handleScrollLeft() {
    const newScrollPosition = scrollPosition - SCROLL_DISTANCE;
    setScrollPosition(newScrollPosition);
    ref!.current!.scrollLeft = newScrollPosition;
    setScrollIndex((prevScrollIndex) => prevScrollIndex - 1);
  }

  function handleScrollRight() {
    const newScrollPosition = scrollPosition + SCROLL_DISTANCE;
    setScrollPosition(newScrollPosition);
    ref!.current!.scrollLeft = newScrollPosition;
    setScrollIndex((prevScrollIndex) => prevScrollIndex + 1);
  }

  return (
    <Wrapper>
      {useChevrons && scrollIndex !== 0 && (
        <SPrevious onClick={handleScrollLeft}>
          <ChevronBackOutlineIc />
        </SPrevious>
      )}
      <List
        variant={variant}
        numberOfCards={numberOfCards}
        ref={ref}
        data-testid={testId}
      >
        {ads?.map((ad, index) => {
          const friendlyPath = convertToFriendlyPath(
            domain,
            ad?.friendlyUrl!,
            undefined,
          );

          return (
            <CardListItem
              key={index}
              data-tracking={cardTrackingId}
              {...accessibleOnClick(
                () => handleCampaignAdRouterPush(friendlyPath),
                'link',
              )}
            >
              <CadCard
                variant={variant}
                orientation={orientation}
                cdnUrl={cdnUrl}
                {...ad}
                hideSave={true}
              />
            </CardListItem>
          );
        })}
      </List>
      {useChevrons &&
        scrollPosition < TOTAL_SCROLL_DISTANCE + carouselCutoff && (
          <SNext onClick={handleScrollRight}>
            <ChevronForwardOutlineIc />
          </SNext>
        )}
    </Wrapper>
  );
}

export { SimpleCarousel };
